import { Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';

// Shows the full text in a tooltip on hover, but only when -webkit-line-clamp is actually cutting it off.
// Used instead of the native title attribute, whose ~1s show delay is set by the browser and can't be changed.
// Touch devices are skipped on purpose: they have no hover, and the clamped text links to the full article.
@Directive({
    selector: '[clampTooltip]'
})
export class ClampTooltipDirective implements OnDestroy {

    @Input() clampTooltip = '';

    private tooltip: HTMLElement | null = null;
    private readonly hide = () => this.hideTooltip();

    constructor(private host: ElementRef<HTMLElement>) { }

    @HostListener('mouseenter')
    onMouseEnter(): void {
        const el = this.host.nativeElement;
        // Checked on each hover rather than once, because clamping depends on the current width.
        const isClamped = el.scrollHeight > el.clientHeight;
        if (!this.clampTooltip || !isClamped || !window.matchMedia('(hover: hover)').matches) {
            return;
        }

        this.showTooltip(el.getBoundingClientRect());
    }

    @HostListener('mouseleave')
    onMouseLeave(): void {
        this.hideTooltip();
    }

    ngOnDestroy(): void {
        this.hideTooltip();
    }

    private showTooltip(anchor: DOMRect): void {
        this.hideTooltip();

        const gap = 8;
        const tooltip = document.createElement('div');
        tooltip.textContent = this.clampTooltip;
        // Screen readers already get the full text: the clamp only hides it visually.
        tooltip.setAttribute('aria-hidden', 'true');

        // Appended to <body> so the host's overflow: hidden can't clip it. Styled inline because
        // Angular's component styles don't reach elements outside the component.
        Object.assign(tooltip.style, {
            position: 'fixed',
            zIndex: '1070',
            width: `${Math.min(Math.max(anchor.width, 280), window.innerWidth - gap * 2)}px`,
            padding: '8px 12px',
            borderRadius: '4px',
            backgroundColor: '#333333',
            color: '#ffffff',
            fontSize: '14px',
            lineHeight: '1.4',
            fontWeight: 'normal',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            pointerEvents: 'none',
        });
        document.body.appendChild(tooltip);

        // Below the text, or above it when there's no room below; kept inside the viewport horizontally.
        let top = anchor.bottom + gap;
        if (top + tooltip.offsetHeight > window.innerHeight) {
            top = anchor.top - gap - tooltip.offsetHeight;
        }
        const left = Math.min(anchor.left, window.innerWidth - gap - tooltip.offsetWidth);
        tooltip.style.top = `${Math.max(gap, top)}px`;
        tooltip.style.left = `${Math.max(gap, left)}px`;

        this.tooltip = tooltip;
        // position: fixed doesn't follow the page, so hide on scroll instead of drifting away from the text.
        window.addEventListener('scroll', this.hide, true);
    }

    private hideTooltip(): void {
        if (!this.tooltip) {
            return;
        }

        this.tooltip.remove();
        this.tooltip = null;
        window.removeEventListener('scroll', this.hide, true);
    }
}
